import React from 'react';
import { Box, TextField, TextFieldProps, Typography, useTheme } from '@mui/material';

interface AttioTextFieldProps extends Omit<TextFieldProps, 'variant'> {
  label: string;
  description?: string;
  required?: boolean;
}

/**
 * Attio-style text field with clean label above and optional description
 * Matches the Attio form aesthetic with larger rounded inputs
 */
export const AttioTextField: React.FC<AttioTextFieldProps> = ({
  label,
  description,
  required = false,
  ...textFieldProps
}) => {
  const theme = useTheme();

  return (
    <Box sx={{ width: '100%' }}>
      {/* Label */}
      <Box sx={{ mb: 0.75 }}>
        <Typography
          component="label"
          sx={{
            fontSize: '13px',
            fontWeight: 500,
            color: theme.palette.text.secondary,
            display: 'block',
            textAlign: 'left'
          }}
        >
          {label}
          {required && (
            <Typography
              component="span"
              sx={{
                color: 'error.main',
                ml: 0.5,
                fontSize: '13px'
              }}
            >
              *
            </Typography>
          )}
        </Typography>
        {description && (
          <Typography
            sx={{
              fontSize: '12px',
              color: theme.palette.text.secondary,
              mt: 0.5,
              lineHeight: 1.4,
              textAlign: 'left'
            }}
          >
            {description}
          </Typography>
        )}
      </Box>

      {/* Input Field */}
      <TextField
        {...textFieldProps}
        required={required}
        fullWidth
        variant="outlined"
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            backgroundColor: theme.palette.background.paper,
            fontSize: '14px',
            padding: 0,
            '& fieldset': {
              borderColor: theme.palette.divider
            },
            '&:hover fieldset': {
              borderColor: theme.palette.mode === 'dark' ? '#3f3f46' : '#d1d5db'
            },
            '&.Mui-focused fieldset': {
              borderColor: theme.palette.primary.main,
              borderWidth: '1px'
            }
          },
          '& .MuiInputBase-input': {
            padding: '8px 12px',
            fontSize: '14px',
            fontWeight: 400,
            textAlign: 'left',
            lineHeight: '1.5'
          },
          '& .MuiInputBase-inputMultiline': {
            padding: '8px 12px',
            fontSize: '14px',
            textAlign: 'left',
            lineHeight: '1.5'
          },
          '& .MuiInputLabel-root': {
            display: 'none' // Hide default label since we have custom one above
          },
          ...textFieldProps.sx
        }}
      />
    </Box>
  );
};

